import { useRef, useState, type ReactNode } from 'react';
import { FiUpload } from 'react-icons/fi';
import { Button } from '@/shared/ui/button';
import { uploadAsset } from '@/modules/media/services/api';
import type { MediaMimeType } from '@/api/schemas/media';

/**
 * The upload control.
 *
 * A file input and a button, and nothing else: the file picker is the browser's
 * own, so the console never invents a second way to choose a file.
 *
 * THE FILE IS NOT SENT TO THE API
 * -------------------------------
 * The bytes go to the object store through the presigned target the API issued.
 * The API never sees them — it observes the stored object itself. That is the
 * contract's shape, not a performance choice.
 *
 * THE PROGRESS SHOWN IS THE TRUTHFUL PART
 * ----------------------------------------
 * There is no percentage: the console cannot know how much of a presigned PUT
 * has landed. It reports the three things it does know — that the upload
 * started, that it finished, and what the server says about the result.
 */
export function UploadControl({
  ownerId,
  onUploaded,
}: {
  ownerId: string;
  onUploaded: () => void;
}): ReactNode {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const start = async (): Promise<void> => {
    const file = inputRef.current?.files?.[0];
    if (!file) {
      setError('Choose a file first.');
      return;
    }

    setIsUploading(true);
    setError(null);
    setMessage('Uploading…');

    try {
      const settled = await uploadAsset({
        ownerId,
        mimeType: file.type as MediaMimeType,
        file,
      });
      setMessage(`Uploaded. The asset is ${settled.status.replace('_', ' ')}.`);
      onUploaded();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setMessage(null);
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef}
        type="file"
        aria-label="Media file"
        accept="image/*,application/pdf,video/mp4,text/plain"
        className="text-sm text-content"
      />
      <Button variant="secondary" size="sm" disabled={isUploading} onClick={() => void start()}>
        <FiUpload size={14} aria-hidden="true" />
        {isUploading ? 'Uploading…' : 'Upload'}
      </Button>
      <UploadFeedback message={message} error={error} />
    </div>
  );
}

/** What the upload is doing, in one line — a status or an alert, never both. */
function UploadFeedback({ message, error }: { message: string | null; error: string | null }): ReactNode {
  if (error !== null) {
    return (
      <span role="alert" className="text-xs text-critical">
        {error}
      </span>
    );
  }

  if (message !== null) {
    return (
      <span role="status" className="text-xs text-content-muted">
        {message}
      </span>
    );
  }

  return null;
}
